import React from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { Tooltip } from '@mui/material';

import { formatDuration } from '../../../../utils/time';
import {
  HeartRateZones,
  ACTIVE_TIME_FORMAT,
  formatNumber,
  getZoneRanges,
} from './workoutHealthData';
import {
  zoneColors,
  HeartIcon,
  SummaryCard,
  SummaryHeader,
  SummaryTitle,
  SyncInfo,
  SyncChip,
  KpiGrid,
  KpiTile,
  KpiLabel,
  KpiValue,
  KpiUnit,
  KpiNote,
  ChartsRow,
  ChartBlock,
  ChartHeader,
  ChartTitle,
  HelpButton,
  ZoneHelp,
  ZoneHelpRow,
  ZoneHelpSwatch,
  ChartUnit,
  DayBars,
  DayBarColumn,
  DayBarValue,
  DayBar,
  EmptyDayBar,
  DayLabels,
  StackedBar,
  StackedSegment,
  ZoneLegend,
  LegendItem,
  Swatch,
  Strong,
  Muted,
} from './styles';
import texts from './texts.json';

const Kpi = ({
  label, value, unit, note,
}) => (
  <KpiTile>
    <KpiLabel>{label}</KpiLabel>
    <KpiValue>
      {value}
      {!!unit && <KpiUnit>{` ${unit}`}</KpiUnit>}
    </KpiValue>
    <KpiNote>{note}</KpiNote>
  </KpiTile>
);

Kpi.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  unit: PropTypes.string,
  note: PropTypes.string,
};

Kpi.defaultProps = {
  unit: '',
  note: '',
};

const formatZoneRange = ({ minBpm, maxBpm }) => {
  if (!minBpm) return format(texts.zoneRangeBelow, { max: maxBpm + 1 });
  if (maxBpm === null) return format(texts.zoneRangeAbove, { min: minBpm });
  return format(texts.zoneRange, { min: minBpm, max: maxBpm });
};

// Zone meanings and bpm ranges, for the "?" tooltip.
const ZonesHelp = ({ maxHeartRate, isMaxHeartRateEstimated }) => (
  <ZoneHelp>
    <span>
      {format(isMaxHeartRateEstimated ? texts.zonesHelpIntroEstimated : texts.zonesHelpIntro, { max: maxHeartRate })}
    </span>
    {getZoneRanges(maxHeartRate).map((zone, index) => (
      <ZoneHelpRow key={zone.id}>
        <ZoneHelpSwatch $color={zoneColors[index]} />
        <span>
          <strong>{`${zone.id} ${zone.label} · ${formatZoneRange(zone)}`}</strong>
          <br />
          {zone.description}
        </span>
      </ZoneHelpRow>
    ))}
  </ZoneHelp>
);

ZonesHelp.propTypes = {
  maxHeartRate: PropTypes.number.isRequired,
  isMaxHeartRateEstimated: PropTypes.bool.isRequired,
};

// The week's health data: totals, calories by day and time in zones.
const WorkoutHealthSummary = ({ summary, maxHeartRate, isMaxHeartRateEstimated }) => {
  const maxDayCalories = Math.max(...summary.caloriesByDay.map(({ calories }) => calories), 1);
  const totalZoneSeconds = summary.zoneSeconds.reduce((sum, seconds) => sum + seconds, 0);

  const renderDayBar = ({
    day, calories, hasData, hasWorkouts,
  }) => {
    if (hasData) {
      return (
        <DayBarColumn key={day}>
          <DayBarValue>{`${formatNumber(calories)} ${texts.kcal}`}</DayBarValue>
          <DayBar
            $height={`${Math.max((calories / maxDayCalories) * 85, 2)}%`}
            $isPeak={calories === maxDayCalories}
          />
        </DayBarColumn>
      );
    }
    return (
      <DayBarColumn key={day}>
        <DayBarValue $muted>{hasWorkouts ? texts.noData : texts.rest}</DayBarValue>
        {hasWorkouts && <EmptyDayBar />}
      </DayBarColumn>
    );
  };

  return (
    <SummaryCard>
      <SummaryHeader>
        <SummaryTitle>
          <HeartIcon $size={18} />
          {texts.title}
        </SummaryTitle>
        <SyncInfo>
          <SyncChip>{format(texts.synced, { synced: summary.syncedCount, total: summary.linkableCount })}</SyncChip>
          {summary.sourceNames.join(' · ')}
        </SyncInfo>
      </SummaryHeader>

      <KpiGrid>
        <Kpi
          label={texts.activeTime}
          value={formatDuration(summary.duration, ACTIVE_TIME_FORMAT)}
          note={texts.acrossSynced}
        />
        <Kpi
          label={texts.activeCalories}
          value={formatNumber(summary.activeCalories)}
          unit={texts.kcal}
          note={texts.acrossSynced}
        />
        <Kpi
          label={texts.totalCalories}
          value={formatNumber(summary.totalCalories)}
          unit={texts.kcal}
          note={texts.activePlusResting}
        />
        <Kpi label={texts.distance} value={summary.distanceKm.toFixed(1)} unit={texts.km} note={texts.acrossSynced} />
        <Kpi
          label={texts.averageHeartRate}
          value={summary.averageHeartRate ? String(summary.averageHeartRate) : '-'}
          unit={summary.averageHeartRate ? texts.bpm : ''}
          note={summary.peakHeartRate
            ? format(texts.peak, { bpm: Math.round(summary.peakHeartRate), day: summary.peakDay })
            : ''}
        />
      </KpiGrid>

      <ChartsRow>
        <ChartBlock>
          <ChartHeader>
            {texts.caloriesByDay}
          </ChartHeader>
          <DayBars>{summary.caloriesByDay.map(renderDayBar)}</DayBars>
          <DayLabels>
            {summary.caloriesByDay.map(({ day }) => <span key={day}>{day.slice(0, 3).toUpperCase()}</span>)}
          </DayLabels>
        </ChartBlock>

        {totalZoneSeconds > 0 && (
          <ChartBlock>
            <ChartHeader>
              <ChartTitle>
                {texts.timeInZones}
                <Tooltip
                  arrow
                  placement="top"
                  title={<ZonesHelp maxHeartRate={maxHeartRate} isMaxHeartRateEstimated={isMaxHeartRateEstimated} />}
                >
                  <HelpButton type="button" aria-label={texts.zonesHelpLabel}>?</HelpButton>
                </Tooltip>
              </ChartTitle>
              <ChartUnit>
                {format(texts.totalTime, { duration: formatDuration(totalZoneSeconds, ACTIVE_TIME_FORMAT) })}
              </ChartUnit>
            </ChartHeader>
            <StackedBar>
              {summary.zoneSeconds.map((seconds, index) => (seconds > 0 ? (
                <StackedSegment
                  key={HeartRateZones[index].id}
                  $share={(seconds / totalZoneSeconds) * 100}
                  $color={zoneColors[index]}
                />
              ) : null))}
            </StackedBar>
            <ZoneLegend>
              {HeartRateZones.map((zone, index) => (
                <LegendItem key={zone.id}>
                  <Swatch $color={zoneColors[index]} />
                  <span>
                    <Strong>{`${zone.id} ${zone.label}`}</Strong>
                    <Muted>{` ${formatDuration(summary.zoneSeconds[index], ACTIVE_TIME_FORMAT)}`}</Muted>
                  </span>
                </LegendItem>
              ))}
            </ZoneLegend>
          </ChartBlock>
        )}
      </ChartsRow>
    </SummaryCard>
  );
};

WorkoutHealthSummary.propTypes = {
  maxHeartRate: PropTypes.number.isRequired,
  isMaxHeartRateEstimated: PropTypes.bool.isRequired,
  summary: PropTypes.shape({
    syncedCount: PropTypes.number,
    linkableCount: PropTypes.number,
    duration: PropTypes.number,
    activeCalories: PropTypes.number,
    totalCalories: PropTypes.number,
    distanceKm: PropTypes.number,
    averageHeartRate: PropTypes.number,
    peakHeartRate: PropTypes.number,
    peakDay: PropTypes.string,
    zoneSeconds: PropTypes.arrayOf(PropTypes.number),
    sourceNames: PropTypes.arrayOf(PropTypes.string),
    caloriesByDay: PropTypes.arrayOf(PropTypes.shape({
      day: PropTypes.string,
      calories: PropTypes.number,
      hasData: PropTypes.bool,
      hasWorkouts: PropTypes.bool,
    })),
  }).isRequired,
};

export default WorkoutHealthSummary;
